// ============================================================
// caseFieldRegistry.ts — Stage 1b fieldKey → store-field mapping.
//
// The live edit-sync engine (stage 1a) drives send/receive off a field's
// data-field attribute (its fieldKey). For CLIENT details the fieldKey suffix
// IS the store field, so no mapping was needed. For the MAIN CASE SECTIONS the
// 2b fieldKeys are LABEL-SLUGS (e.g. `accident.driver-name`) — decoupled from
// both the store column (`driverName`) and the nested path
// (`accidentDetails.driverName`). flattenCase is forward-only (nested→flat), so
// there is no reverse map. This file IS that reverse map.
//
// Each entry is a pair of closures:
//   read(case)         → the current value(s) this fieldKey owns (for the
//                        broadcast payload on blur).
//   apply(case, value) → a Partial<Case> (the nested shape updateCase wants)
//                        that writes the received value back. Fed straight to
//                        applyRemoteCaseEdit, which advances the autosave
//                        baselines so NO save-loop fires.
//
// Why an explicit registry (not a re-key): the case sections include genuine
// composites a 1:1 re-key can't express — the Injury "Injuries" grid is ONE
// fieldKey covering 11 columns, and `injuries.description-of-injuries` writes
// clientDetails (a different object). Closures handle those cleanly, and the
// typed builders below make a wrong store-field name a COMPILE error rather
// than a silent wrong-field write.
// ============================================================
import { useCasevoStore } from '../store/useCasevoStore'
import type { Case, Address, InjuryDetails } from '../types/case.types'

export interface CaseFieldEntry {
  read: (c: Case) => unknown
  apply: (c: Case, value: unknown) => Partial<Case>
}

// Sections that are a single-level nested object on Case. `field` is typed as
// keyof Case[S], so a mistyped store field fails to compile.
type ObjSection = 'accidentDetails' | 'injuryDetails' | 'clientSource' | 'defendant' | 'tpi'
const obj = <S extends ObjSection, F extends keyof Case[S]>(section: S, field: F): CaseFieldEntry => ({
  read: (c) => (c[section] as unknown as Record<string, unknown>)[field as string],
  apply: (c, v) => ({ [section]: { ...(c[section] as object), [field]: v } }) as Partial<Case>,
})

// Top-level flat Case columns (medical*, tps*, funding).
const flat = <F extends keyof Case>(field: F): CaseFieldEntry => ({
  read: (c) => c[field],
  apply: (c, v) => ({ [field]: v }) as Partial<Case>,
})

// Defendant / TPI carry a nested Address | null. Rebuild the full address from
// the current value (null → all-empty), overlay the changed sub-field — exactly
// what each section's updateAddress helper does, so the save shape matches.
const nestedAddr = <S extends 'defendant' | 'tpi'>(section: S, sub: keyof Address): CaseFieldEntry => ({
  read: (c) => c[section].address?.[sub] ?? '',
  apply: (c, v) => {
    const a = c[section].address
    const base: Address = {
      line1: a?.line1 ?? '', line2: a?.line2 ?? '', town: a?.town ?? '',
      county: a?.county ?? '', postcode: a?.postcode ?? '',
    }
    return { [section]: { ...c[section], address: { ...base, [sub]: v } } } as Partial<Case>
  },
})

// ── Composite: the Injury "Injuries" checkbox grid ───────────
// ONE fieldKey (`injuries.injuries`) wraps 11 body-part checkboxes, each
// writing a different injuryDetails boolean. read emits all 11 as an object;
// apply overlays whichever keys the payload carries. (exceptionallySevere has
// its own key; exceptionallySevereDetails is edited in a modal with no
// data-field and is intentionally NOT synced.)
const INJURY_GRID_FIELDS: (keyof InjuryDetails)[] = [
  'neck', 'shoulder', 'back', 'ribsChestTorso', 'faceCheckboneJawNose',
  'forearmWristHandFingers', 'damageToTeeth', 'headSenses', 'hipsPelvisGenitals',
  'legKneeAnkleFootToe', 'psychological',
  // B3 (B2c item 4)
  'hearingEars', 'lungsBreathing', 'skin', 'internalOrgans', 'fatal', 'other',
]
const injuryGrid: CaseFieldEntry = {
  read: (c) => {
    const o: Record<string, boolean> = {}
    for (const k of INJURY_GRID_FIELDS) o[k] = c.injuryDetails[k] as boolean
    return o
  },
  apply: (c, v) => {
    const payload = (v && typeof v === 'object') ? (v as Record<string, unknown>) : {}
    const next = { ...c.injuryDetails }
    for (const k of INJURY_GRID_FIELDS) {
      if (k in payload) (next as Record<string, unknown>)[k] = !!payload[k]
    }
    return { injuryDetails: next }
  },
}

// ── Composite: the Accident "Accident Location" wrapper ──────
// The outer FormField `accident.accident-location` directly owns the mode
// toggle (accidentLocationMode) + the free-text textarea (accidentLocationFreeText).
// The address sub-fields have their OWN inner fieldKeys (accident.town etc.), so
// closest('[data-field]') resolves them to those, not here.
// NOTE (Stage 4): accidentLocationFreeText is now a Yjs collab rich-text field
// (doc `case:<id>:accidentLocationFreeText`), so its CONTENT is owned by
// Hocuspocus, not the near-live engine. But the MODE toggle (address ↔ freetext)
// still syncs here so the free-text editor appears/disappears live for other
// viewers when someone flips the mode (mirrors the exceptionally-severe reveal
// fix). So read/apply carry accidentLocationMode ONLY — the free-text content is
// intentionally NOT broadcast (Yjs would double-sync it).
const accidentLocation: CaseFieldEntry = {
  read: (c) => ({
    accidentLocationMode: c.accidentDetails.accidentLocationMode,
  }),
  apply: (c, v) => {
    const o = (v && typeof v === 'object') ? (v as Record<string, unknown>) : {}
    const next = { ...c.accidentDetails }
    if ('accidentLocationMode' in o) {
      next.accidentLocationMode = o.accidentLocationMode as Case['accidentDetails']['accidentLocationMode']
    }
    return { accidentDetails: next }
  },
}

// ── THE REGISTRY — fieldKey (label-slug) → entry ─────────────
// Verified 1:1 against each section's FormField fieldKey + its input's onChange
// store write. Address-SEARCH boxes are intentionally absent (not stored
// fields). Keys not present here simply don't sync (graceful).
const CASE_FIELD_REGISTRY: Record<string, CaseFieldEntry> = {
  // ── Accident (accidentDetails) ──
  'accident.askcue-reference': obj('accidentDetails', 'askCueReference'),
  'accident.accident-date': obj('accidentDetails', 'accidentDate'),
  'accident.accident-time': obj('accidentDetails', 'accidentTime'),
  'accident.driver-or-passenger': obj('accidentDetails', 'vehiclePosition'),
  'accident.driver-name': obj('accidentDetails', 'driverName'),
  'accident.accident-description': obj('accidentDetails', 'accidentDescription'),
  'accident.please-describe': obj('accidentDetails', 'accidentDescriptionOther'),
  'accident.accident-location': accidentLocation,
  'accident.address-line-1': obj('accidentDetails', 'accidentLocationAddressLine1'),
  'accident.address-line-2': obj('accidentDetails', 'accidentLocationAddressLine2'),
  'accident.town': obj('accidentDetails', 'accidentLocationAddressTown'),
  'accident.county': obj('accidentDetails', 'accidentLocationAddressCounty'),
  'accident.postcode': obj('accidentDetails', 'accidentLocationAddressPostcode'),
  'accident.wearing-a-seatbelt': obj('accidentDetails', 'wearingSeatbelt'),
  'accident.claimant-reg': obj('accidentDetails', 'claimantReg'),
  'accident.was-the-claimant-a-passenger-in-the-vehicle-responsible': obj('accidentDetails', 'claimantVehicleResponsible'),
  'accident.number-of-people-in-the-claimant-vehicle': obj('accidentDetails', 'numberOfPeopleInVehicle'),
  'accident.police-involvement': obj('accidentDetails', 'policeInvolvement'),
  // ── Injury (injuryDetails; description lives on clientDetails) ──
  'injuries.injuries': injuryGrid,
  'injuries.exceptionally-severe': obj('injuryDetails', 'exceptionallySevere'),
  // injuries.description-of-injuries is now a Yjs collab rich-text field (Stage
  // 2) — it syncs via Hocuspocus (doc `case:<id>:descriptionOfInjuries`), NOT
  // the near-live field-edit engine. Intentionally omitted so resolveCaseField
  // returns null for it (no double-sync). The FormField focus/presence indicator
  // still works (separate data-field focus channel). (exceptionallySevereDetails
  // was never in this registry — it's edited in a modal with no data-field.)
  // ── Defendant (defendant + nested address) ──
  'defendant.reg': obj('defendant', 'reg'),
  'defendant.name': obj('defendant', 'name'),
  'defendant.address-line-1': nestedAddr('defendant', 'line1'),
  'defendant.address-line-2': nestedAddr('defendant', 'line2'),
  'defendant.town': nestedAddr('defendant', 'town'),
  'defendant.county': nestedAddr('defendant', 'county'),
  'defendant.postcode': nestedAddr('defendant', 'postcode'),
  'defendant.main-contact-number': obj('defendant', 'mainContactNumber'),
  // ── TPI (tpi + nested address) ──
  'tpi.insurer': obj('tpi', 'insurer'),
  'tpi.reference-number': obj('tpi', 'referenceNumber'),
  'tpi.address-line-1': nestedAddr('tpi', 'line1'),
  'tpi.address-line-2': nestedAddr('tpi', 'line2'),
  'tpi.town': nestedAddr('tpi', 'town'),
  'tpi.county': nestedAddr('tpi', 'county'),
  'tpi.postcode': nestedAddr('tpi', 'postcode'),
  // ── Medical Agency (flat) ──
  'medicalAgency.agency-name': flat('medicalAgencyName'),
  'medicalAgency.address-line-1': flat('medicalAgencyAddressLine1'),
  'medicalAgency.address-line-2': flat('medicalAgencyAddressLine2'),
  'medicalAgency.town': flat('medicalAgencyAddressTown'),
  'medicalAgency.county': flat('medicalAgencyAddressCounty'),
  'medicalAgency.postcode': flat('medicalAgencyAddressPostcode'),
  'medicalAgency.phone': flat('medicalAgencyPhone'),
  'medicalAgency.email': flat('medicalAgencyEmail'),
  // medicalAgency.notes is now a Yjs collab rich-text field (Stage 1) — it syncs
  // via Hocuspocus (doc `case:<id>:medicalAgencyNotes`), NOT the near-live
  // field-edit engine. Intentionally NOT in this registry so resolveCaseField
  // returns null for it (no double-sync). The FormField focus/presence indicator
  // still works (that's the separate data-field focus channel).
  // ── Medical Expert (flat) ──
  'medicalExpert.expert-name': flat('medicalExpertName'),
  'medicalExpert.address-line-1': flat('medicalExpertAddressLine1'),
  'medicalExpert.address-line-2': flat('medicalExpertAddressLine2'),
  'medicalExpert.town': flat('medicalExpertAddressTown'),
  'medicalExpert.county': flat('medicalExpertAddressCounty'),
  'medicalExpert.postcode': flat('medicalExpertAddressPostcode'),
  'medicalExpert.phone': flat('medicalExpertPhone'),
  'medicalExpert.email': flat('medicalExpertEmail'),
  // medicalExpert.notes → Yjs collab (doc `case:<id>:medicalExpertNotes`), see
  // medicalAgency.notes note above. Excluded from the field-edit engine.
  // ── TPS (flat) ──
  'tps.solicitor-name': flat('tpsName'),
  'tps.address-line-1': flat('tpsAddressLine1'),
  'tps.address-line-2': flat('tpsAddressLine2'),
  'tps.town': flat('tpsAddressTown'),
  'tps.county': flat('tpsAddressCounty'),
  'tps.postcode': flat('tpsAddressPostcode'),
  'tps.phone': flat('tpsPhone'),
  'tps.email': flat('tpsEmail'),
  // tps.notes → Yjs collab (doc `case:<id>:tpsNotes`), see medicalAgency.notes
  // note above. Excluded from the field-edit engine.
  // ── Funding (flat) ──
  'funding.deduction': flat('deductionMode'),
  'funding.custom-deduction': flat('deductionCustomValue'),
  'funding.ate-provider': flat('ateProvider'),
  // ── Client Source (clientSource) ──
  'clientSource.client-source-name': obj('clientSource', 'name'),
  'clientSource.contact-number': obj('clientSource', 'contactNumber'),
  'clientSource.email-address': obj('clientSource', 'emailAddress'),
  'clientSource.address-line-1': obj('clientSource', 'addressLine1'),
  'clientSource.address-line-2': obj('clientSource', 'addressLine2'),
  'clientSource.town': obj('clientSource', 'addressTown'),
  'clientSource.county': obj('clientSource', 'addressCounty'),
  'clientSource.postcode': obj('clientSource', 'addressPostcode'),
}

// Resolve a case-room fieldKey to its read/apply entry, or null if it isn't a
// syncable case field. Covers BOTH the case sections (the registry above) and
// the case's clientDetails snapshot (`clientDetails.*`, resolved dynamically
// against the live object so the address-search slugs — which aren't own
// properties — are excluded, matching stage 1a behaviour).
export const resolveCaseField = (key: string): CaseFieldEntry | null => {
  const direct = CASE_FIELD_REGISTRY[key]
  if (direct) return direct
  if (key.startsWith('clientDetails.')) {
    const field = key.slice('clientDetails.'.length)
    const details = useCasevoStore.getState().currentCase?.clientDetails
    if (!details || !Object.prototype.hasOwnProperty.call(details, field)) return null
    return {
      read: (c) => (c.clientDetails as unknown as Record<string, unknown>)[field],
      apply: (c, v) => ({ clientDetails: { ...c.clientDetails, [field]: v } }) as Partial<Case>,
    }
  }
  return null
}
